import animation from '@helpers/animation';
import brand from '@helpers/brand';
import fonts from '@helpers/fonts';
import { baseControl, baseControlUnderline } from '@stories/Elements/Forms/Fields/Form.styles';
import styled, { css } from 'styled-components';
import { TextboxTheme } from './Textbox';

export const TextboxInput = styled.input.attrs({ type: 'text' })`
  ${baseControl};
`;

export const NumericInput = styled.input.attrs({ type: 'number' })`
  ${baseControl};
`;

export const EmailInput = styled.input.attrs({ type: 'email' })`
  ${baseControl};
`;

export const TextboxThemeStyles = css`
  ${({ theme }) => {
    switch (theme) {
      default:
      case TextboxTheme.Red: {
        return css`
          border-bottom: 2px solid ${brand.white};
          background-color: ${brand.red.dark};

          color: ${brand.white};

          &::placeholder {
            color: ${brand.white};
          }

          &:-webkit-autofill {
            -webkit-text-fill-color: ${brand.white};
          }

          &:focus {
            border-color: ${brand.white};
          }
        `;
      }

      case TextboxTheme.Red_Base: {
        return css`
          border: none;
          border-bottom: 2px solid ${brand.white};
          background-color: ${brand.red.base};

          color: ${brand.white};

          &::placeholder {
            color: ${brand.white};
          }

          &:-webkit-autofill {
            -webkit-text-fill-color: ${brand.white};
          }

          &:focus {
            border-color: ${brand.white};
          }
        `;
      }

      case TextboxTheme.White: {
        return css`
          border-bottom: 2px solid ${brand.grey[25]};
          background-color: ${brand.white};
          color: ${brand.black};

          &::placeholder {
            color: ${brand.black};
          }

          &:-webkit-autofill {
            -webkit-text-fill-color: ${brand.black};
          }

          &:focus {
            border-bottom: 2px solid ${brand.grey[25]};
          }

          :disabled {
            opacity: 0.6;
          }
        `;
      }

      case TextboxTheme.Black: {
        return css`
          border-bottom: 2px solid ${brand.grey[25]};

          color: ${brand.black};

          &::placeholder {
            color: ${brand.grey[55]};
          }

          &:-webkit-autofill {
            -webkit-text-fill-color: ${brand.black};
          }
        `;
      }

      case TextboxTheme.White_Text: {
        return css`
          border-bottom: 2px solid ${brand.white};

          color: ${brand.white};

          &::placeholder {
            color: ${brand.white};
          }

          &:-webkit-autofill {
            -webkit-text-fill-color: ${brand.white};
          }
        `;
      }
    }
  }};
`;

export const TextboxUnderlineInput = styled.input.attrs((props) => ({
  disabled: props.disabled,
  type: props.type ?? 'text',
}))<{
  theme?: TextboxTheme;
  errorStyle?: boolean;
  reduceWidth?: boolean;
}>`
  ${baseControlUnderline};
  ${TextboxThemeStyles};

  ${({ errorStyle }) =>
    errorStyle
      ? css`
          border: 2px solid currentColor;
        `
      : null}

  ${({ reduceWidth }) =>
    reduceWidth
      ? css`
          max-width: 50%;
        `
      : null}

  &:-webkit-autofill {
    transition: background-color 5000s ease-in-out 0s;
  }

  &:focus {
    /* Important needed to overwrite legacy styles */
    box-shadow: none !important;
  }
`;

export const PasswordTextbox = styled.input.attrs({ autoComplete: 'new-password', type: 'password' })`
  ${baseControl};
`;

export const PasswordTextboxUnderline = styled.input.attrs((props) => ({
  autoComplete: props.autoComplete ?? 'new-password',
  type: props.type ?? 'password',
}))<{ theme?: TextboxTheme }>`
  ${baseControlUnderline};
  ${TextboxThemeStyles};

  &:-webkit-autofill {
    transition: background-color 5000s ease-in-out 0s;
  }

  &:focus {
    /* Important needed to overwrite legacy styles */
    box-shadow: none !important;
  }
`;

export const labelUnderlineBase = css<{ isActive?: boolean }>`
  ${fonts.helvetica['53-extended']};
  position: absolute;
  left: 0;
  top: -5px;
  display: block;
  margin: 0;
  font-size: 11px;
  line-height: 14px;
  color: ${brand.white};
  opacity: 1;
  transform: translateY(-100%);
  transition: opacity 0.3s ${animation.easeInOutCubic}, transform 0.3s ${animation.easeInOutCubic};
  z-index: 1;
  user-select: none;

  ${({ isActive }) =>
    isActive
      ? css`
          opacity: 1;
          transform: translateY(-100%);

          ${TextboxUnderlineInput}:not([type='checkbox']):not([type='radio']):placeholder-shown + &,
          ${PasswordTextboxUnderline}:not([type='checkbox']):not([type='radio']):placeholder-shown + & {
            opacity: 1;
            transform: translateY(-100%);
          }
        `
      : css`
          opacity: 0;
          transform: translateY(0);
        `}
`;

export const LabelUnderline = styled.label<{ isActive?: boolean; theme?: TextboxTheme }>`
  ${labelUnderlineBase};

  ${({ theme }) => {
    switch (theme) {
      default:
      case TextboxTheme.White: {
        return css`
          color: ${brand.white};
        `;
      }

      case TextboxTheme.Black: {
        return css`
          color: ${brand.grey[55]};
        `;
      }
    }
  }};

  ${TextboxUnderlineInput}:not([type='checkbox']):not([type='radio']):placeholder-shown + &,
  textarea:placeholder-shown + &,
  [data-datepicker-placeholder='true'][data-placeholder='true'] + &,
  ${PasswordTextboxUnderline}:not([type='checkbox']):not([type='radio']):placeholder-shown + & {
    opacity: 0;
    transform: translateY(0);
  }

  ${TextboxUnderlineInput}:not([type='checkbox']):not([type='radio']):not(:placeholder-shown) + &, 
  textarea:not(:placeholder-shown) + &,
  [data-datepicker-placeholder='true'][data-placeholder='false'] + &,
  ${PasswordTextboxUnderline}:not([type='checkbox']):not([type='radio']):not(:placeholder-shown) + & {
    opacity: 1;
    transform: translateY(-100%);
  }
`;
